import React, { useState, useEffect } from 'react';
import api from '../../config/axios';
import { itemTotal, orderSubtotal, orderTax, taxRateLabel } from '../../utils/tax';
import Header from '../common/Header';
import './AllOrders.css';
import { useNavigate } from 'react-router-dom';
import AddItemsModal from './AddItemsModal';

interface AllOrdersProps {
    user: any;
    onLogout: () => void;
}

const AllOrders: React.FC<AllOrdersProps> = ({ user, onLogout }) => {
    const [orders, setOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState<string>(() => {
      const today = new Date();
      return today.toISOString().split('T')[0];
    });
    const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
    const [addItemsOrderId, setAddItemsOrderId] = useState<string | null>(null);
    const navigate = useNavigate();
    const [autoRefresh, setAutoRefresh] = useState(true);

    const fetchOrders = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/api/orders');
        setOrders(res.data.data);
      } catch (err: any) {
        setError('Không thể tải đơn hàng');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchOrders();
    }, []);

    useEffect(() => {
      if (!autoRefresh) return;

      const interval = setInterval(() => {
        fetchOrders();
      }, 30000);

      return () => clearInterval(interval);
    }, [autoRefresh]);

    const filteredOrders = selectedDate
      ? orders.filter(order => {
          const orderDate = new Date(order.createdAt).toISOString().split('T')[0];
          return orderDate === selectedDate;
        })
      : orders;

    const handleDateChange = (date: string) => {
      setSelectedDate(date);
    };

    const handleShowAll = () => {
      setSelectedDate('');
    };

    const handleOrderClick = (order: any) => {
      setSelectedOrder(order);
    };

    return (
      <>
        <Header user={user} onLogout={onLogout}>
          <span className="user-role">{user.role === 'admin' ? 'Quản lý' : 'Nhân viên'}</span>
        </Header>
        <div className="all-orders-container">

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
            <button
              className="all-orders-back-btn"
              onClick={() => navigate('/')}
              title="Quay lại Dashboard"
              style={{
                background: 'none',
                border: 'none',
                fontSize: 28,
                cursor: 'pointer',
                marginRight: 12
              }}
            >
              ←
            </button>
            <h2 className="all-orders-title" style={{ margin: 0, flex: 1 }}>TẤT CẢ ĐƠN HÀNG</h2>
          </div>

          <div className="all-orders-filters" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
            <input
              type="date"
              value={selectedDate}
              onChange={e => handleDateChange(e.target.value)}
              className="all-orders-date-input"
            />
            <button
              onClick={handleShowAll}
              className={`all-orders-filter-btn${selectedDate ? ' active' : ''}`}
            >
              {selectedDate ? 'Hiển thị tất cả' : 'Lọc theo ngày'}
            </button>
          </div>
          {loading && <div>Đang tải...</div>}
          {error && <div className="all-orders-error">{error}</div>}
          <table className="all-orders-table">
            <thead>
              <tr>
                <th>Mã đơn</th>
                <th>Thời gian</th>
                <th>Bàn</th>
                <th>Tổng tiền</th>
                <th>Nhân viên</th>
                <th>Số món</th>
                <th>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr key={order._id} onClick={() => handleOrderClick(order)} style={{ cursor: 'pointer' }}>
                  <td>{order._id.slice(-6).toUpperCase()}</td>
                  <td>{new Date(order.createdAt).toLocaleString()}</td>
                  <td>
                    {order.mergedTables && order.mergedTables.length > 0
                      ? order.mergedTables.map((table: any) => table.name).join(', ')
                      : order.tableId?.name || 'Không xác định'
                    }
                  </td>
                  <td>{order.total.toLocaleString()} đ</td>
                  <td>{order.staff?.username || 'N/A'}</td>
                  <td>{order.items.length}</td>
                  <td>
                    {order.status === 'pending' ? 'Chờ thanh toán' :
                     order.status === 'paid' ? 'Đã thanh toán' :
                     order.status === 'processing' ? 'Đang xử lý' :
                     order.status === 'cancelled' ? 'Đã hủy' :
                     order.status === 'merged' ? 'Đã gộp' :
                     order.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {selectedOrder && (
          <div className="all-orders-detail-modal">
            <div className="all-orders-detail-content">
              <h3>Chi tiết đơn hàng #{selectedOrder._id.slice(-6).toUpperCase()}</h3>
              <div className="all-orders-detail-info">
                <div><b>Nhân viên:</b> {
                  selectedOrder.staffName ||
                  selectedOrder.staff?.username ||
                  selectedOrder.staffId?.username ||
                  (typeof selectedOrder.staffId === 'object' ? selectedOrder.staffId.username : selectedOrder.staffId) ||
                  'Không xác định'
                }</div>
                <div><b>Thời gian:</b> {new Date(selectedOrder.createdAt).toLocaleString()}</div>
                <div><b>Bàn:</b> {
                  selectedOrder.mergedTables && selectedOrder.mergedTables.length > 0
                    ? selectedOrder.mergedTables.map((table: any) => table.name).join(', ')
                    : selectedOrder.tableId?.name || 'Không xác định'
                }</div>
                <div><b>Tổng tiền:</b> {selectedOrder.total.toLocaleString()} đ</div>
                <div><b>Số món:</b> {selectedOrder.items.length}</div>
                <div><b>Trạng thái:</b> {
                  selectedOrder.status === 'pending' ? 'Chờ thanh toán' :
                  selectedOrder.status === 'paid' ? 'Đã thanh toán' :
                  selectedOrder.status === 'processing' ? 'Đang xử lý' :
                  selectedOrder.status === 'cancelled' ? 'Đã hủy' :
                  selectedOrder.status === 'merged' ? 'Đã gộp' :
                  selectedOrder.status
                }</div>
                {selectedOrder.paymentMethod && (
                  <div><b>Phương thức thanh toán:</b> {selectedOrder.paymentMethod}</div>
                )}
                {selectedOrder.paidAt && (
                  <div><b>Thời gian thanh toán:</b> {new Date(selectedOrder.paidAt).toLocaleString()}</div>
                )}
              </div>
              <div className="all-orders-items-section">
                <h4>Danh sách món</h4>
                <table className="all-orders-items-table">
                  <thead>
                    <tr>
                      <th>Ảnh</th>
                      <th>Món</th>
                      <th>Giá</th>
                      <th>Số lượng</th>
                      <th>Thành tiền</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map((item: any, idx: number) => {
                      const lineTotal = itemTotal(item, selectedOrder.taxRate || 0);

                      return (
                        <tr key={idx}>
                          <td><img src={item.imageUrl} alt={item.name} className="all-orders-item-img" /></td>
                          <td>{item.name}</td>
                          <td>{item.price.toLocaleString()} đ</td>
                          <td>{item.quantity}</td>
                          <td>{Math.round(lineTotal).toLocaleString()} đ</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="all-orders-summary-section">
                <h4>Tổng kết</h4>
                <div className="all-orders-summary">
                  {(() => {
                    // Thuế của đơn = tổng thuế từng món.
                    const calculatedSubtotal = orderSubtotal(selectedOrder);
                    const calculatedTax = orderTax(selectedOrder);

                    return (
                      <>
                        <div className="all-orders-summary-item">
                          <span>Tạm tính:</span>
                          <span>{calculatedSubtotal.toLocaleString()} đ</span>
                        </div>
                        <div className="all-orders-summary-item">
                          <span>Thuế ({taxRateLabel(selectedOrder)}):</span>
                          <span>{Math.round(calculatedTax).toLocaleString()} đ</span>
                        </div>
                        <div className="all-orders-summary-item">
                          <span>Giảm giá:</span>
                          <span>-{(selectedOrder.discount || 0).toLocaleString()} đ</span>
                        </div>
                        <div className="all-orders-summary-item all-orders-summary-total">
                          <span>Tổng cộng:</span>
                          <span>{selectedOrder.total.toLocaleString()} đ</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="all-orders-detail-actions">
                {(selectedOrder.status === 'pending' || selectedOrder.status === 'processing') && (
                  <button
                    className="all-orders-add-items-btn"
                    onClick={() => setAddItemsOrderId(selectedOrder._id)}
                  >
                    + Thêm món
                  </button>
                )}
                <button
                  className="all-orders-detail-close-btn"
                  onClick={() => setSelectedOrder(null)}
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        )}

        {addItemsOrderId && (
          <AddItemsModal
            orderId={addItemsOrderId}
            onClose={() => setAddItemsOrderId(null)}
            onAdded={async () => {
              await fetchOrders();
              // Cập nhật lại chi tiết đang mở
              try {
                const res = await api.get('/api/orders');
                const fresh = res.data.data.find((o: any) => o._id === addItemsOrderId);
                if (fresh) setSelectedOrder(fresh);
              } catch {}
            }}
          />
        )}
      </>
    );
};

export default AllOrders;